import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'utils/translation';
import { useModal } from '../../../../components/v2/Modal/useModal';
import { useModalContent } from '../../../../components/v2/Modal/useModalContent';
import { createModalFormFooter } from '../../../../components/v2/Modal/createModalFormFooter';
import Spacer from '../../../../components/v2/Spacer/Spacer';
import {
  mapFieldToProps,
  mapValuesToPayload,
} from '../../../../modules/intelligent-layer/utils';
import { useCreateAssetSeriesSpec, useCreateDeviceSeries } from '../hooks';
import Typography from 'components/v2/Typography/Typography';
import Section from 'components/v2/Section/Section';
import Button, { ButtonGroup } from 'components/v2/Button/Button';
import Stack from '@mui/material/Stack/Stack';
import { refetchActiveQueries } from '../../../../utils/QueryClientProvider';
import FluentForm from '../../../../components/v2/Form/FluentForm';
import Form from '../../../../components/v2/Form/Form';
import { FieldType } from '../../../../components/v2/Form/types';
import { Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useNewDeviceFields } from '../../../../modules/intelligent-layer/hooks';
import { useShallowEqualEffect } from '../../../../utils/use-shallow-effect';
import repeat from '../../../../utils/repeat';
import Skeleton from '@mui/material/Skeleton';
import http from '../../../../utils/http';
import AccountQuotaGuard from '../../../../modules/users/components/AccountQuotaGuard';

const ADD_MANY_DEVICES_FORM_ID = 'plugin-device-add-series';

const AddManyDevicesModalFooter = createModalFormFooter(
  ADD_MANY_DEVICES_FORM_ID,
  { submitKey: 'Add' },
);

export type AddManyDevicesSuccessData = {
  deviceType: {
    id: number;
    name: string;
  };
  items: {
    identifier: string;
    status: 'ok' | 'error';
    message: string;
  }[];
};

const SuccessNotice = ({ data }: { data: AddManyDevicesSuccessData }) => {
  const success = data.items.filter((item) => item.status === 'ok');
  const errors = data.items.filter((item) => item.status !== 'ok');
  const firstSuccess = success[0] ?? null;

  return (
    <Section>
      <Stack direction="column">
        <Typography>
          <Trans
            i18nKey="assets.add_series.devices_added_info"
            values={{
              assetsCount: success.length,
              deviceTypeLabel: data.deviceType.name,
            }}
            components={{ strong: <strong /> }}
          />
          <br />
          {firstSuccess && (
            <Trans
              i18nKey="assets.add_series.id_starting_from_info"
              values={{ identifier: firstSuccess.identifier }}
              components={{ strong: <strong /> }}
            />
          )}
        </Typography>
        <Spacer size={4} />
        {errors.length > 0 && (
          <>
            <strong>
              <Trans
                i18nKey="assets.add_series.errors_count_info"
                values={{ count: errors.length }}
                components={{ strong: <strong /> }}
              />
            </strong>
            <Spacer size={2} />
            <Typography color="error">
              {errors.map((item) => item.identifier).join(', ')}
            </Typography>
            <Spacer size={4} />
          </>
        )}
      </Stack>
    </Section>
  );
};

function AddManyDevicesSuccessModalFooter({ close }: { close(): void }) {
  const { t } = useTranslation();

  return (
    <ButtonGroup align="right">
      <Button size="small" onClick={close}>
        {t('Close')}
      </Button>
    </ButtonGroup>
  );
}

type Props = {
  onClose(): void;
  onSuccess?(data: AddManyDevicesSuccessData): void;
  defaultType?: { label: string; value: number } | null;
  allowTypeChange?: boolean;
};

const MinimumPossibleNumberWatcher = ({ id, query }: any) => {
  const { watch, setValue } = useFormContext();
  const prefix = watch('prefix');

  const handleStartFromCheck = useCallback(() => {
    http
      .get<
        never,
        { startFrom: number; prefix: string }
      >(`/api/device/add-series/${id}`, { params: { prefix } })
      .then((response) => {
        setValue('startFrom', response.startFrom);
      });
  }, [prefix]);

  const checkPrefix = useCallback(() => {
    http
      .get<never, { prefix: string }>(`/api/device/add-series/${id}`)
      .then((response) => {
        if (!prefix && response.prefix) setValue('prefix', response.prefix);
      });
  }, [id, prefix]);

  useShallowEqualEffect(() => {
    void handleStartFromCheck();
  }, [handleStartFromCheck]);

  useShallowEqualEffect(() => {
    void checkPrefix();
  }, [id]);

  return null;
};

const AddManyDevicesForm = ({
  onSuccess,
  defaultType = null,
  allowTypeChange = true,
}: Props) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<any | null>(defaultType);
  const createSeriesSpecQuery = useCreateAssetSeriesSpec(selectedType?.value);
  const { data = [], isLoading } = useNewDeviceFields(selectedType);
  const mutation = useCreateDeviceSeries();
  const modalContent = useModalContent();
  let deviceTypeField = data.find((item) => item.name === 'device_type');

  if (!deviceTypeField) {
    deviceTypeField = data.find((item) => item.name === 'type');
  }

  let deviceTypeProps = null;

  if (deviceTypeField) {
    deviceTypeProps = mapFieldToProps(deviceTypeField);
  }

  const multiDeviceFields = data
    ?.filter((item) => item.name !== 'device_type')
    .filter((item) => item.settings.enabledInSeries);

  useEffect(() => {
    if (mutation.isError) {
      modalContent.scrollToTop();
    }
  }, [mutation.isError, modalContent]);

  const onSubmit = async (values: any) => {
    try {
      const result = (await mutation.mutateAsync(
        mapValuesToPayload(values),
      )) as AddManyDevicesSuccessData;
      void refetchActiveQueries();
      onSuccess?.(result);
      return result;
    } catch (error: any) {
      return Promise.reject({
        response: error?.response,
      });
    }
  };

  if (isLoading || createSeriesSpecQuery.isLoading) {
    return (
      <Section padding="small">
        {repeat(7, (i) => (
          <React.Fragment key={i}>
            <Skeleton height={16} width="200px" />
            <Skeleton height={64} />
            <Spacer size={2} />
          </React.Fragment>
        ))}
      </Section>
    );
  }

  return (
    <Section padding="small">
      <FluentForm
        onChange={(values) => {
          if (values.hasOwnProperty('device_type')) {
            setSelectedType(values['device_type']);
          }
        }}
        action={onSubmit}
        defaultValues={{
          device_type: selectedType,
          prefix: createSeriesSpecQuery.data?.prefix,
          startFrom: createSeriesSpecQuery.data?.startFrom,
        }}
        formId={ADD_MANY_DEVICES_FORM_ID}
        renderButtons={false}
      >
        {selectedType && (
          <MinimumPossibleNumberWatcher
            id={selectedType.value}
            query={createSeriesSpecQuery}
          />
        )}
        {allowTypeChange && deviceTypeProps && (
          <Form.Field
            {...deviceTypeProps}
            rules={{ required: true }}
            autoFocus
          />
        )}
        {multiDeviceFields.map((item) => (
          <Form.Field {...mapFieldToProps(item)} />
        ))}
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Form.Field
              label={t('assets.add_series_count')}
              name="assetsCount"
              type={FieldType.Text}
              rules={{ required: true }}
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={5}>
            <Form.Field
              label={t('assets.add_series_prefix')}
              name="prefix"
              type={FieldType.Text}
              placeholder=""
              rules={{ required: true }}
            />
          </Grid>
          <Grid item xs={5}>
            <Form.Field
              label={t('assets.add_series_start_from')}
              name="startFrom"
              type={FieldType.Text}
              placeholder=""
              rules={{ required: true }}
            />
          </Grid>
        </Grid>
      </FluentForm>
    </Section>
  );
};

export const useCreateManyDevicesForm = () => {
  const modal = useModal();
  const { t } = useTranslation();

  return (
    props: Omit<
      React.ComponentProps<typeof AddManyDevicesForm>,
      'onClose'
    > = {},
  ) => {
    modal.open(
      ({ close }) => (
        <AccountQuotaGuard quota="maxAssets">
          <AddManyDevicesForm
            {...props}
            onClose={close}
            onSuccess={(data) => {
              props.onSuccess?.(data);
              queueMicrotask(() => {
                close();
                modal.open(() => <SuccessNotice data={data} />, {
                  title: t('assets.add_series_title'),
                  size: 'medium',
                  footer: AddManyDevicesSuccessModalFooter,
                });
              });
            }}
          />
        </AccountQuotaGuard>
      ),
      {
        title: t('assets.add_series_title'),
        disableBackdropClose: true,
        footer: AddManyDevicesModalFooter,
      },
    );
  };
};
